/**
 * SaaS Billing service — thin client for the CleanBid SaaS Stripe endpoints.
 *
 * Sprint B — surfaces only. NO entitlement enforcement is performed here.
 * Reads are authoritative from Firestore mirror (server side); the frontend
 * simply displays what the mirror already reports.
 *
 * NOTE: This service is STRICTLY separate from the existing tenant→customer
 * Stripe/Connect flows (services/pricingService.js, pages/Billing.jsx,
 * pages/Subscriptions.jsx, pages/CardOnFile.jsx). Do not cross-wire.
 */

import { getAuth } from 'firebase/auth';

const API = process.env.REACT_APP_BACKEND_URL || '';

async function authHeaders() {
  const token = await getAuth().currentUser?.getIdToken(false);
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token || ''}`,
  };
}

async function handle(res) {
  const text = await res.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }
  if (!res.ok) {
    const err = new Error(body?.detail || body?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export async function getBillingConfig() {
  const res = await fetch(`${API}/api/billing/config`, { headers: await authHeaders() });
  return handle(res);
}

export async function getSubscription() {
  const res = await fetch(`${API}/api/billing/subscription`, { headers: await authHeaders() });
  return handle(res);
}

/** Sprint 306 — Seat usage vs plan cap for the current company. */
export async function getSeatUsage() {
  const res = await fetch(`${API}/api/billing/seat-usage`, { headers: await authHeaders() });
  return handle(res);
}

export async function createCheckoutSession({ planKey, withTrial }) {
  const res = await fetch(`${API}/api/billing/checkout`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ planKey, withTrial: !!withTrial }),
  });
  return handle(res);
}

export async function createPortalSession() {
  const res = await fetch(`${API}/api/billing/portal`, {
    method: 'POST',
    headers: await authHeaders(),
    body: '{}',
  });
  return handle(res);
}

// UI catalog — kept in the frontend for display only. Server is authoritative
// on price via Stripe lookup_key AND on seat enforcement via /api/billing/seat-usage.
// Sprint 306: distinction is team size, NOT artificial feature restrictions —
// all plans receive the full CleanBid core platform.
export const PLAN_CATALOG = [
  {
    planKey: 'starter',
    name: 'Starter',
    price: 79,
    priceLabel: '$79',
    cadence: '/month',
    maxUsers: 3,
    tagline: 'For solo operators and small crews.',
    features: [
      'Full CleanBid core platform',
      'Up to 3 users',
    ],
    highlight: false,
  },
  {
    planKey: 'pro',
    name: 'Pro',
    price: 179,
    priceLabel: '$179',
    cadence: '/month',
    maxUsers: 7,
    tagline: 'For growing multi-tech teams.',
    features: [
      'Full CleanBid core platform',
      'Up to 7 users',
    ],
    highlight: true,
  },
  {
    planKey: 'enterprise',
    name: 'Enterprise',
    price: 349,
    priceLabel: '$349',
    cadence: '/month',
    maxUsers: null,   // null = unlimited
    tagline: 'For established multi-location operators.',
    features: [
      'Full CleanBid core platform',
      'Unlimited users',
    ],
    highlight: false,
  },
];

// Purely presentational — never used as an entitlement gate in Sprint B.
export function statusLabel(status) {
  if (!status) return 'No subscription';
  const map = {
    trialing: 'Trialing',
    active: 'Active',
    past_due: 'Past due',
    canceled: 'Canceled',
    unpaid: 'Unpaid',
    incomplete: 'Incomplete',
    incomplete_expired: 'Incomplete (expired)',
    internal_grandfathered: 'Internal',
  };
  return map[status] || status;
}
